/**
 * Geld- und Mengenarithmetik.
 *
 * Grundregel: In der Datenbank und in allen Berechnungen gibt es keine
 * Gleitkommazahlen fuer Geld. Alles sind ganze Zahlen in einer festen
 * kleinsten Einheit:
 *
 *   - Betraege (Summen, Steuer, Positionswerte) in **Cent**       -> 1234 =  12,34 EUR
 *   - Einzelpreise in **Zehntausendstel Euro** (4 Nachkommastellen) -> 12345 = 1,2345 EUR
 *   - Mengen in **Tausendstel Einheit** (3 Nachkommastellen)        -> 2500  = 2,5 Stueck
 *
 * Einzelpreise brauchen mehr Stellen als Betraege, weil im Handel mit
 * Staffelpreisen und Fremdwaehrungsumrechnung sonst systematisch falsch
 * gerundet wird (z.B. 0,0349 EUR/Stueck bei 10.000 Stueck).
 */
/** Skalierungsfaktoren der jeweiligen Festkomma-Einheit. */
export const CENT_SCALE = 100;
export const PRICE_SCALE = 10_000;
export const QTY_SCALE = 1_000;
/**
 * Kaufmaennisches Runden: ab .5 wird vom Nullpunkt weg gerundet.
 *
 * `Math.round` rundet immer Richtung +unendlich (-0,5 -> -0), was bei
 * Gutschriften und Stornos zu Betraegen fuehrt, die sich nicht exakt
 * gegen die Ursprungsrechnung aufheben.
 */
export function roundCommercial(value) {
    return value < 0 ? -Math.round(-value) : Math.round(value);
}
/**
 * Wandelt eine deutsche Zahleneingabe in eine Festkommazahl.
 *
 * Akzeptiert "1.234,56", "1234,56", "1234.56" und "1 234,56". Punkt gilt
 * als Tausendertrennzeichen, sobald ein Komma vorhanden ist; steht nur ein
 * Punkt in der Eingabe, wird er als Dezimaltrennzeichen gelesen.
 *
 * @returns die Zahl in der Zieleinheit oder `null`, wenn die Eingabe kein
 *          gueltiger Zahlenwert ist.
 */
export function parseDecimal(input, scale) {
    const raw = input.trim().replace(/\s/g, '');
    if (raw === '')
        return null;
    const negative = raw.startsWith('-');
    let body = negative ? raw.slice(1) : raw;
    if (body.startsWith('+'))
        body = body.slice(1);
    if (body.includes(',')) {
        body = body.replace(/\./g, '').replace(',', '.');
    }
    else {
        // Nur Punkte: "1.234" ist mehrdeutig. Genau ein Punkt mit maximal
        // zwei Nachkommastellen lesen wir als Dezimaltrennzeichen, alles
        // andere als Tausenderpunkte.
        const parts = body.split('.');
        if (parts.length > 2 || (parts.length === 2 && parts[1].length === 3)) {
            body = parts.join('');
        }
    }
    if (!/^\d*(\.\d*)?$/.test(body) || body === '' || body === '.')
        return null;
    const [wholePart = '0', fractionPart = ''] = body.split('.');
    const digits = String(scale).length - 1;
    const fraction = fractionPart.padEnd(digits + 1, '0');
    const whole = Number(wholePart) * scale;
    const scaled = Number(fraction.slice(0, digits) || '0');
    // Eine Stelle mehr als noetig einlesen, damit z.B. 1,005 EUR korrekt
    // auf 1,01 EUR gerundet wird statt abgeschnitten zu werden.
    const guard = Number(fraction[digits] ?? '0') >= 5 ? 1 : 0;
    const value = whole + scaled + guard;
    if (!Number.isFinite(value))
        return null;
    return negative ? -value : value;
}
/** Deutsche Zahleneingabe -> Cent. `"1.234,56"` -> `123456` */
export function parseAmount(input) {
    return parseDecimal(input, CENT_SCALE);
}
/** Deutsche Zahleneingabe -> Zehntausendstel Euro. `"1,2345"` -> `12345` */
export function parsePrice(input) {
    return parseDecimal(input, PRICE_SCALE);
}
/** Deutsche Zahleneingabe -> Tausendstel Einheit. `"2,5"` -> `2500` */
export function parseQuantity(input) {
    return parseDecimal(input, QTY_SCALE);
}
/** Formatiert eine Festkommazahl deutsch, ohne Waehrungssymbol. */
export function formatDecimal(value, scale, options = {}) {
    const digits = String(scale).length - 1;
    const { minFractionDigits = digits, maxFractionDigits = digits, grouping = true, } = options;
    return (value / scale).toLocaleString('de-DE', {
        minimumFractionDigits: minFractionDigits,
        maximumFractionDigits: maxFractionDigits,
        useGrouping: grouping,
    });
}
/** Cent -> `"1.234,56"` */
export function formatAmount(cent) {
    return formatDecimal(cent, CENT_SCALE);
}
/** Cent -> `"1.234,56 EUR"` */
export function formatAmountWithCurrency(cent, currency = 'EUR') {
    return `${formatAmount(cent)} ${currency === 'EUR' ? '€' : currency}`;
}
/**
 * Einzelpreis -> deutsche Darstellung.
 *
 * Zeigt mindestens zwei, bei Bedarf bis zu vier Nachkommastellen, damit
 * 1,2345 EUR nicht als 1,23 EUR erscheint, 10,00 EUR aber auch nicht als
 * 10,0000 EUR.
 */
export function formatPrice(price) {
    return formatDecimal(price, PRICE_SCALE, { minFractionDigits: 2, maxFractionDigits: 4 });
}
/** Menge -> deutsche Darstellung, ohne unnoetige Nullen. `2500` -> `"2,5"` */
export function formatQuantity(qty) {
    return formatDecimal(qty, QTY_SCALE, { minFractionDigits: 0, maxFractionDigits: 3 });
}
/**
 * Wert einer Belegposition in Cent.
 *
 * Menge x Einzelpreis, danach der Rabatt. Es wird genau einmal am Ende
 * gerundet, nicht zwischendurch.
 *
 * @param quantity        Menge in Tausendstel
 * @param unitPrice       Einzelpreis in Zehntausendstel Euro
 * @param discountPercent Rabatt in Hundertstel Prozent (1250 = 12,50 %)
 */
export function lineAmount(quantity, unitPrice, discountPercent = 0) {
    const gross = (quantity * unitPrice) / (QTY_SCALE * (PRICE_SCALE / CENT_SCALE));
    const net = gross * (1 - discountPercent / 10_000);
    return roundCommercial(net);
}
/** Summiert Cent-Betraege. */
export function sumAmounts(amounts) {
    return amounts.reduce((total, amount) => total + amount, 0);
}
//# sourceMappingURL=money.js.map